import React, { useState, useEffect, useRef } from 'react';
import { Share2, Linkedin, Twitter, Facebook, Mail, Link as LinkIcon, Check, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Reusable share button with a dropdown menu (LinkedIn, X, Facebook, WhatsApp, Email, Copy).
 *
 * Props:
 *   - title:   document/post title
 *   - url:     direct, public, shareable URL (will be encoded)
 *   - message: optional intro line (default: "Découvrez ce rapport de CRESA ASBL :")
 *   - size:    'sm' (default) | 'md'
 *   - variant: 'icon' (icon-only round button) | 'pill' (icon + label)
 *   - testId:  data-testid prefix
 */
export default function ShareButton({
  title,
  url,
  message = 'Découvrez ce rapport de CRESA ASBL :',
  size = 'sm',
  variant = 'pill',
  testId = 'share',
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const fullText = `${message} « ${title} »`;
  const encodedUrl = encodeURIComponent(url || '');
  const encodedText = encodeURIComponent(fullText);
  const encodedSubject = encodeURIComponent(`CRESA ASBL — ${title}`);
  const encodedBody = encodeURIComponent(`${fullText}\n\n${url}\n\n— CRESA ASBL, Îlots de Développement Intégral en RD Congo`);

  const links = [
    {
      key: 'linkedin',
      label: 'LinkedIn',
      icon: Linkedin,
      color: 'text-[#0A66C2]',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      icon: MessageCircle,
      color: 'text-[#25D366]',
      href: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
    },
    {
      key: 'twitter',
      label: 'X (Twitter)',
      icon: Twitter,
      color: 'text-[#0F1419]',
      href: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    },
    {
      key: 'facebook',
      label: 'Facebook',
      icon: Facebook,
      color: 'text-[#1877F2]',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      key: 'email',
      label: 'Email',
      icon: Mail,
      color: 'text-[#5C665A]',
      href: `mailto:?subject=${encodedSubject}&body=${encodedBody}`,
    },
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${fullText}\n${url}`);
      setCopied(true);
      toast.success('Lien copié dans le presse-papiers');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn('Clipboard copy failed:', err);
      toast.error('Impossible de copier le lien');
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `CRESA ASBL — ${title}`, text: fullText, url });
        setOpen(false);
        return true;
      } catch (err) {
        // AbortError = user cancelled the native picker; not an error to surface.
        // Other errors fall through to the desktop dropdown fallback.
        if (err?.name !== 'AbortError') {
          console.warn('Native share failed:', err);
        }
      }
    }
    return false;
  };

  const triggerSize = size === 'md' ? 'px-4 py-2 text-sm' : 'px-3 py-1.5 text-xs';
  const iconSize = size === 'md' ? 16 : 14;

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={async () => {
          // On mobile, try native share first; fallback to dropdown on desktop or refusal
          const used = await handleNativeShare();
          if (!used) setOpen((v) => !v);
        }}
        data-testid={`${testId}-button`}
        className={`inline-flex items-center gap-1.5 font-bold rounded-md border-2 border-[#4A6B48] text-[#4A6B48] hover:bg-[#4A6B48] hover:text-white transition-all ${triggerSize}`}
        title="Partager"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Share2 size={iconSize} />
        {variant === 'pill' && <span>Partager</span>}
      </button>

      {open && (
        <div
          className="absolute right-0 z-40 mt-2 w-56 bg-white border-2 border-[#E5E3DB] rounded-lg shadow-xl overflow-hidden"
          role="menu"
          data-testid={`${testId}-menu`}
        >
          <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-[#5C665A] border-b border-[#E5E3DB] bg-[#F9F8F6]">
            Partager via
          </div>
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <a
                key={l.key}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid={`${testId}-${l.key}`}
                className="flex items-center gap-3 px-3 py-2 text-sm text-[#2E332D] hover:bg-[#F9F8F6] transition-colors"
              >
                <Icon size={16} className={l.color} />
                <span>{l.label}</span>
              </a>
            );
          })}
          <button
            type="button"
            onClick={handleCopy}
            data-testid={`${testId}-copy`}
            className="flex w-full items-center gap-3 px-3 py-2 text-sm text-[#2E332D] hover:bg-[#F9F8F6] border-t border-[#E5E3DB] transition-colors"
          >
            {copied ? <Check size={16} className="text-[#4A6B48]" /> : <LinkIcon size={16} className="text-[#5C665A]" />}
            <span>{copied ? 'Lien copié !' : 'Copier le lien'}</span>
          </button>
        </div>
      )}
    </div>
  );
}
